/**
 * Module dependencies.
 */

import { RouteName } from 'i18n-routes';
import { color, media, units } from 'src/styles/utils';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { normalizeNewLine } from 'src/core/utils/strings';
import { theme } from 'styled-tools';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Container from 'src/components/core/layout/container';
import Loading from 'src/components/core/loading';
import Metatags, { HrefLang } from 'src/components/metatags';
import Navigation from 'src/components/navigation';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement, useMemo } from 'react';
import Richtext from 'src/components/core/richtext';
import Type from 'src/components/core/typography';
import closeIcon from 'src/assets/svg/close.svg';
import styled from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * Export `PageProps` type.
 */

export type PageProps = {
  description: string,
  descriptionSeo?: string | null,
  id: string,
  slug: string,
  subtitle?: string | null,
  text?: string | null,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps;
  hrefLangs?: HrefLang[];
  isKiosk?: boolean;
  routeName?: RouteName;
  slug: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('beige200')};
  min-height: 100vh;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(5)}) 0 ${units(7)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(9.5)}) 0 ${units(19)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas: '. content .';
    grid-template-columns: 1fr 8fr 3fr;
  `}

  ${media.min('md')`
    grid-template-columns: 1fr 7fr 4fr;
  `}

  ${media.min('lg')`
    grid-template-columns: 1fr 6fr 5fr;
  `}
`;

/**
 * `StyledNavigation` styled component.
 */

const StyledNavigation = styled(Navigation)`
  grid-area: navigation;
  margin-bottom: ${units(5.5)};

  ${media.min('ms')`
    padding-left: ${units(3.5)};
  `}
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  grid-area: content;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2).attrs({ as: 'h1' })`
  margin-bottom: ${units(6)};
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'span'
})`
  display: block;
  grid-area: subTitle;
  margin-bottom: ${units(2)};
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  margin-bottom: ${units(6)};
`;

/**
 * `Legal` component.
 */

const Legal = (props: Props): ReactElement => {
  const {
    data: pageData, 
    hrefLangs: pageHrefLangs, 
    isKiosk,
    routeName,
    slug
  } = props;

  const router = useRouter();
  const { translate } = useTranslate();
  const {
    data,
    isLoading,
    isSuccess
  } = usePage<PageProps>({ initialData: pageData, slug });

  const hrefLangs = useMemo(() => 
    pageHrefLangs || getHrefLangs(data?.translations, routeName), 
    [data?.translations, pageHrefLangs, routeName]
  );

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={data?.descriptionSeo}
        hrefLangs={hrefLangs}
        isKiosk={isKiosk}
        pageTitle={data?.title}
        title={data?.titleSeo}
      />

      <Wrapper>
        <Loading active={isLoading} />

        {isSuccess && (
          <Container hasPadding={!isKiosk}>
            <Grid>
              <ContentWrapper>
                <StyledNavigation
                  icon={closeIcon}
                  label={translate('common:actions.close')}
                  onClick={() => router.back()}
                />

                <Title>
                  {`${data?.title} `}

                  <SubTitle>
                    {data?.subtitle}
                  </SubTitle>

                </Title>

                <RawHtml element={Description}>
                  {normalizeNewLine(data?.description)}
                </RawHtml>

                {data?.text && (
                  <Richtext>
                    {data?.text}
                  </Richtext>
                )}
              </ContentWrapper>
            </Grid>
          </Container>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `Legal` component.
 */

export default Legal;
